import { GIFT_PASS_BUNDLE_MAX_TAGS, type GiftPassTagIcon } from '@bonsai/shared';
import { Plus } from 'lucide-react';
import { useRef } from 'react';

import { EditorFieldError } from '../../shared/editor-page/EditorFieldError';
import { EditorFormSection } from '../../shared/editor-page/EditorFormSection';

import { newTagDraft, type BundleFormErrors, type BundleTagDraft } from './bundle-form';
import { BundleTagIconPalette } from './BundleTagIconPalette';
import { BundleTagRow } from './BundleTagRow';

import './gift-pass-bundle-editor.css';

export interface BundleTagsSectionProps {
  tags: BundleTagDraft[];
  errors: BundleFormErrors;
  onChange: (tags: BundleTagDraft[]) => void;
}

interface Caret {
  key: string;
  start: number;
  end: number;
}

/** The text with `[code]` in place of the selection, and where the caret goes after it. */
export function insertIconCode(text: string, icon: GiftPassTagIcon, start: number, end: number) {
  const code = `[${icon}]`;
  const before = text.slice(0, start);
  const after = text.slice(end);
  const lead = before && !before.endsWith(' ') ? ' ' : '';
  const trail = after.startsWith(' ') ? '' : ' ';
  const inserted = `${lead}${code}${trail}`;
  return { text: `${before}${inserted}${after}`, caret: before.length + inserted.length };
}

export function BundleTagsSection({ tags, errors, onChange }: BundleTagsSectionProps) {
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const caret = useRef<Caret | null>(null);
  const full = tags.length >= GIFT_PASS_BUNDLE_MAX_TAGS;

  const patchTag = (key: string, patch: Partial<BundleTagDraft>) =>
    onChange(tags.map((tag) => (tag.key === key ? { ...tag, ...patch } : tag)));

  const focusAt = (key: string, position: number) =>
    requestAnimationFrame(() => {
      const input = inputs.current.get(key);
      input?.focus();
      input?.setSelectionRange(position, position);
    });

  const insertIcon = (icon: GiftPassTagIcon) => {
    const target = tags.find((tag) => tag.key === caret.current?.key) ?? tags.at(-1);
    if (!target) {
      const tag = newTagDraft(`[${icon}] `);
      onChange([tag]);
      focusAt(tag.key, tag.text.length);
      return;
    }
    const at = caret.current?.key === target.key ? caret.current : null;
    const end = target.text.length;
    const next = insertIconCode(target.text, icon, at?.start ?? end, at?.end ?? end);
    patchTag(target.key, { text: next.text });
    caret.current = { key: target.key, start: next.caret, end: next.caret };
    focusAt(target.key, next.caret);
  };

  return (
    <EditorFormSection
      title="Tags"
      titleId="bundle-tags-title"
      hint={`Up to ${GIFT_PASS_BUNDLE_MAX_TAGS} short labels on the bundle’s card on the purchase page.`}
      last
    >
      {tags.map((tag, index) => (
        <BundleTagRow
          key={tag.key}
          tag={tag}
          index={index}
          error={errors[`tags.${index}`]}
          inputRef={(el) => {
            if (el) inputs.current.set(tag.key, el);
            else inputs.current.delete(tag.key);
          }}
          onChange={(patch) => patchTag(tag.key, patch)}
          onRemove={() => onChange(tags.filter((t) => t.key !== tag.key))}
          onCaret={(start, end) => (caret.current = { key: tag.key, start, end })}
        />
      ))}
      {errors.tags && <EditorFieldError id="bundle-tags-error">{errors.tags}</EditorFieldError>}
      {!full && (
        <button
          type="button"
          className="nb-btn gpb-ed-add-tag"
          onClick={() => {
            const tag = newTagDraft();
            onChange([...tags, tag]);
            focusAt(tag.key, 0);
          }}
        >
          <Plus size={14} aria-hidden />
          Add tag
        </button>
      )}
      <BundleTagIconPalette onPick={insertIcon} />
    </EditorFormSection>
  );
}
